// rc148 — Hooks del módulo WireGuard (servidor wg-byteisp).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  PagedWgPeers,
  RouterWireguardActivarResponse,
  RouterWireguardInfo,
  WgAplicacion,
  WgEstado,
  WgMensajeAplicacion,
  WgPeerCreateInput,
  WgPeerMutacionResponse,
  WgPeerUpdateInput,
  WgScript,
} from "./wgSchemas";

const KEY = "wg-peers";
const KEY_ESTADO = "wg-estado";
const KEY_ROUTER = "wg-router";

function useInvalidarWg() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: [KEY] });
    qc.invalidateQueries({ queryKey: [KEY_ESTADO] });
    qc.invalidateQueries({ queryKey: [KEY_ROUTER] });
  };
}

export function useWgEstado(opts?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: [KEY_ESTADO],
    queryFn: () => apiCall<WgEstado>("get", "/api/v1/wireguard/estado"),
    refetchInterval: opts?.refetchInterval ?? 30_000,
  });
}

export function useWgPeers(
  filters: { page?: number; size?: number; search?: string },
  opts?: { refetchInterval?: number },
) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => {
      const p: Record<string, string | number> = {
        page: filters.page ?? 1,
        size: filters.size ?? 50,
      };
      if (filters.search) p.search = filters.search;
      return apiCall<PagedWgPeers>("get", "/api/v1/wireguard/peers", { searchParams: p });
    },
    placeholderData: (prev) => prev,
    refetchInterval: opts?.refetchInterval ?? 30_000,
  });
}

export function useCrearWgPeer() {
  const invalidar = useInvalidarWg();
  return useMutation({
    mutationFn: (data: WgPeerCreateInput) =>
      apiCall<WgPeerMutacionResponse>("post", "/api/v1/wireguard/peers", { json: data, timeout: 30_000 }),
    onSuccess: invalidar,
  });
}

export function useEditarWgPeer(id: string) {
  const invalidar = useInvalidarWg();
  return useMutation({
    mutationFn: (data: WgPeerUpdateInput) =>
      apiCall<WgPeerMutacionResponse>("patch", `/api/v1/wireguard/peers/${id}`, { json: data, timeout: 30_000 }),
    onSuccess: invalidar,
  });
}

export function useRegenerarWgPeer() {
  const invalidar = useInvalidarWg();
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<WgPeerMutacionResponse>("post", `/api/v1/wireguard/peers/${id}/regenerar`, { json: {}, timeout: 30_000 }),
    onSuccess: invalidar,
  });
}

export function useBorrarWgPeer() {
  const invalidar = useInvalidarWg();
  return useMutation({
    mutationFn: (id: string) =>
      apiCall<WgMensajeAplicacion>("delete", `/api/v1/wireguard/peers/${id}`, { timeout: 30_000 }),
    onSuccess: invalidar,
  });
}

export function useAplicarWg() {
  const invalidar = useInvalidarWg();
  return useMutation({
    mutationFn: () => apiCall<WgAplicacion>("post", "/api/v1/wireguard/aplicar", { json: {}, timeout: 30_000 }),
    onSuccess: invalidar,
  });
}

export function useWgScript(id: string | null) {
  return useQuery({
    queryKey: [KEY, "script", id],
    queryFn: () => apiCall<WgScript>("get", `/api/v1/wireguard/peers/${id}/script-mikrotik`),
    enabled: !!id,
    // Contiene la llave privada: no se cachea más allá del diálogo abierto.
    gcTime: 0,
    staleTime: 0,
  });
}

// ─── Router: pasar a WireGuard / regresar a OpenVPN ──────────────────────
export function useRouterWireguard(routerId: string | null) {
  return useQuery({
    queryKey: [KEY_ROUTER, routerId],
    queryFn: () => apiCall<RouterWireguardInfo>("get", `/api/v1/routers/${routerId}/wireguard`),
    enabled: !!routerId,
    refetchInterval: 30_000,
  });
}

function useInvalidarRouter() {
  const qc = useQueryClient();
  const invalidarWg = useInvalidarWg();
  return () => {
    invalidarWg();
    qc.invalidateQueries({ queryKey: ["routers"] });
  };
}

export function useActivarWireguard() {
  const invalidar = useInvalidarRouter();
  return useMutation({
    mutationFn: (routerId: string) =>
      apiCall<RouterWireguardActivarResponse>(
        "post", `/api/v1/routers/${routerId}/wireguard/activar`, { json: {}, timeout: 30_000 },
      ),
    onSuccess: (res) => { if (res.ok) invalidar(); },
  });
}

export function useRevertirWireguard() {
  const invalidar = useInvalidarRouter();
  return useMutation({
    mutationFn: (routerId: string) =>
      apiCall<RouterWireguardInfo>(
        "post", `/api/v1/routers/${routerId}/wireguard/revertir`, { json: {} },
      ),
    onSuccess: invalidar,
  });
}
